import { useState } from "react";
import * as Yup from "yup";
import { useFormik } from "formik";
import classNames from "classnames";
import styles from "./style.module.scss";
import CustomModal from "shared/components/common/customModal";
import { Icons } from "assets";
import CustomButton from "shared/components/common/customButton";
import CustomInput from "shared/components/common/customInput";
import CustomSelect from "shared/components/common/customSelect";
import CustomTextEditor from "shared/components/common/customTextEditor";
import { CreateBlogService } from "shared/services/blogs";
import { toastMessage } from "shared/components/common/toast";

interface Props extends ModalProps {
    onSuccess?: () => void;
}

const categoryOptions = [
    { value: "Legal Updates", title: "Legal Updates" },
    { value: "Resources and Tools", title: "Resources & Tools" },
    { value: "Blogs", title: "Blogs" },
];

const validationSchema = Yup.object({
    title: Yup.string().required("Title is required"),
    category: Yup.string().required("Category is required"),
    content: Yup.string().required("Content is required"),
    cover: Yup.mixed().required("Cover image is required"),
});

const CreateBlogModal = ({ show, handleClose, onSuccess }: Props) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [coverFile, setCoverFile] = useState<File | null>(null);
    const [coverPreview, setCoverPreview] = useState<string | null>(null);

    const formik = useFormik({
        initialValues: {
            title: "",
            category: "",
            content: "",
            cover: null as File | null,
        },
        validationSchema,
        onSubmit: async (values) => {
            if (!coverFile) {
                toastMessage("error", "Please upload a cover image");
                return;
            }

            setLoading(true);
            try {
                await CreateBlogService(
                    values.category,
                    values.title,
                    coverFile,
                    values.content
                );
                toastMessage("success", "Blog created successfully!");
                formik.resetForm();
                setCoverFile(null);
                setCoverPreview(null);
                handleClose();
                onSuccess?.();
            } catch (error: any) {
                const errorMessage =
                    error?.response?.data?.message ||
                    "Failed to create blog. Please try again.";
                toastMessage("error", errorMessage);
                console.error("Error creating blog:", error);
            } finally {
                setLoading(false);
            }
        },
    });

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setCoverFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setCoverPreview(reader.result as string);
            };
            reader.readAsDataURL(file);
            formik.setFieldValue("cover", file);
        }
    };

    const handleRemoveImage = () => {
        setCoverFile(null);
        setCoverPreview(null);
        formik.setFieldValue("cover", null);
    };

    const handleCloseModal = () => {
        if (!loading) {
            formik.resetForm();
            setCoverFile(null);
            setCoverPreview(null);
            handleClose();
        }
    };

    return show ? (
        <CustomModal onClose={handleCloseModal}>
            <div className={classNames(styles.contentContainer)}>
                <div
                    className={classNames(
                        "flex items-center justify-between px-4 mt-4 mb-3"
                    )}
                >
                    <h3 className={styles.modalTitle}>Create Blog</h3>
                    <div
                        className={classNames(styles.crossContainer)}
                        onClick={handleCloseModal}
                    >
                        <Icons.Cross />
                    </div>
                </div>

                <form
                    onSubmit={formik.handleSubmit}
                    className={classNames(styles.modalBody, "w-full flex flex-col gap-4")}
                >
                    <CustomInput
                        label="Title"
                        placeholder="Enter Blog Title"
                        name="title"
                        value={formik.values.title}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                            formik.touched.title && formik.errors.title
                                ? formik.errors.title
                                : ""
                        }
                    />

                    <CustomSelect
                        options={categoryOptions}
                        value={formik.values.category}
                        label="Category"
                        placeholder="Select a Category"
                        onChange={(selectedOption: any) =>
                            formik.setFieldValue("category", selectedOption.value)
                        }
                        error={
                            formik.touched.category && formik.errors.category
                                ? formik.errors.category
                                : ""
                        }
                    />

                    <div>
                        <label className={classNames(styles.label, "label")}>
                            Blog Content
                        </label>
                        <div className="pt-2">
                            <CustomTextEditor
                                value={formik.values.content}
                                onChange={(value) => formik.setFieldValue("content", value)}
                                onBlur={() => formik.setFieldTouched("content", true)}
                                placeholder="Enter blog content here..."
                            />
                            {formik.touched.content && formik.errors.content && (
                                <div className="error">{formik.errors.content}</div>
                            )}
                        </div>
                    </div>

                    <div>
                        <label className={classNames(styles.label, "label")}>
                            Cover Image
                        </label>
                        <div className="pt-2">
                            {coverPreview ? (
                                <div className={classNames(styles.filePreviewContainer)}>
                                    <img
                                        src={coverPreview}
                                        alt="Blog Cover Preview"
                                        className={classNames(styles.previewImage)}
                                    />
                                    <span
                                        className={classNames(styles.removeButton)}
                                        onClick={handleRemoveImage}
                                    >
                                        <Icons.Cross />
                                    </span>
                                </div>
                            ) : (
                                <div className={styles.fileUploadContainer}>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleFileChange}
                                        className={styles.fileInput}
                                        id="cover-upload"
                                    />
                                    <label htmlFor="cover-upload" className={styles.fileLabel}>
                                        <Icons.CameraPlus />
                                        <span>Click to upload or drag and drop</span>
                                        <span className={styles.fileHint}>
                                            PNG, JPG, GIF up to 10MB
                                        </span>
                                    </label>
                                </div>
                            )}
                            {formik.touched.cover && formik.errors.cover && (
                                <div className="error">{formik.errors.cover}</div>
                            )}
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 mt-4">
                        <CustomButton
                            type="button"
                            title="Cancel"
                            containerStyle="outlined-btn-primary"
                            onClick={handleCloseModal}
                            disabled={loading}
                        />
                        <CustomButton
                            type="submit"
                            loading={loading}
                            title="Create Blog"
                        />
                    </div>
                </form>
            </div>
        </CustomModal>
    ) : null;
};

export default CreateBlogModal;
